import {useEffect, useState} from "react";
import {Button, Checkbox, FormControlLabel} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import CardsContainer from "@/components/CardsContainer";
import CardsTable from "@/components/CardsTable";
import CardsList from "@/components/CardsList";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import Loading from "@/components/Loading";
import {VenueAPI} from "@/api/venue";
import {ClubAPI} from "@/api/club";
import {
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	getInternalClubId,
} from "@/utils/constants";
export default function VenueManager({clubId, handleNotification}) {
	const [venues, setVenues] = useState([]),
		[clubs, setClubs] = useState([]),
		[loading, setLoading] = useState(true),
		[editing, setEditing] = useState(null),
		[deleting, setDeleting] = useState(null);
	const [linking, setLinking] = useState(false),
		[allVenues, setAllVenues] = useState([]),
		[linkedVenue, setLinkedVenue] = useState(null);
	const myClubId = getInternalClubId();
	const load = async () => {
		setLoading(true);
		try {
			setVenues(await VenueAPI.list(clubId ? {clubId} : undefined));
			const allClubs = (await ClubAPI.list()) || [];
			if (myClubId && !allClubs.some((c) => c.id === myClubId))
				allClubs.push(await ClubAPI.getById(myClubId));
			setClubs(allClubs);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		load();
	}, [clubId]);
	const edit = (v) =>
		setEditing(
			v
				? {...v, clubIds: v.clubIds || []}
				: {name: "", address: "", clubIds: clubId ? [clubId] : []},
		);
	const error = (e) =>
		handleNotification(e.response?.data?.error || ERROR_OPERATION, "error");
	const clubNames = (v) =>
		(v.clubIds || [])
			.map((id) => clubs.find((c) => c.id === id)?.name || "")
			.filter(Boolean)
			.join(", ");
	const save = async () => {
		setLoading(true);
		try {
			const body = {
				name: editing.name.trim(),
				address: editing.address?.trim() || null,
				clubIds: editing.clubIds,
			};
			if (editing.id) await VenueAPI.update(editing.id, body);
			else await VenueAPI.create(body);
			setEditing(null);
			await load();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (e) {
			error(e);
		} finally {
			setLoading(false);
		}
	};
	const columns = [
		{label: "Nome", key: "name", align: "start"},
		{label: "Indirizzo completo", key: "address", align: "start"},
		{label: "Club", key: "clubIds", customRender: clubNames, align: "start"},
	];
	const actions = [
		{icon: <EditIcon />, variant: "outlined", title: "Modifica", onClick: edit},
		{
			icon: <DeleteIcon />,
			variant: "outlined",
			title: clubId ? "Rimuovi associazione" : "Elimina",
			color: "error",
			onClick: setDeleting,
		},
	];
	return (
		<>
			<Loading visible={loading} />
			<div className="d-flex align-items-center justify-content-between">
				<strong>{clubId ? "Campi del club" : "Campi"}</strong>
				{clubId && (
					<Button
						variant="outlined"
						size="small"
						title="Associa campo"
						aria-label="Associa campo"
						onClick={async () => {
							try {
								setAllVenues(await VenueAPI.list());
								setLinkedVenue(null);
								setLinking(true);
							} catch (e) {
								error(e);
							}
						}}
					>
						<LinkIcon />
					</Button>
				)}
				<Button
					variant="outlined"
					size="small"
					title="Aggiungi campo"
					aria-label="Aggiungi campo"
					onClick={() => edit(null)}
				>
					<AddIcon />
				</Button>
			</div>
			{clubId && (
				<p>I campi associati vengono proposti per gli allenamenti del club.</p>
			)}
			<CardsContainer
				tableComponent={
					<CardsTable
						fullHeight={false}
						data={venues}
						configurationColumns={columns}
						fullWidth
						handleEdit={edit}
						handleDelete={setDeleting}
						defaultSortedField="name"
					/>
				}
				listComponent={
					<CardsList
						fitHeight
						listConfiguration={{
							data: venues,
							title: (v) => v.name,
							subtitle: (v) => v.address || "Indirizzo da completare",
							text: clubNames,
							actions,
						}}
						defaultSortedField="name"
					/>
				}
			/>
			<VLFModal
				open={linking}
				title="Associa campo al club"
				close={() => setLinking(false)}
				disabled={loading || !linkedVenue}
				content={
					<VLFSelectNew
						label="Campo"
						options={allVenues
							.filter((v) => !v.clubIds.includes(clubId))
							.map((v) => ({value: v.id, label: v.name, ...v}))}
						value={linkedVenue}
						onChange={setLinkedVenue}
					/>
				}
				confirm={async () => {
					setLoading(true);
					try {
						await VenueAPI.update(linkedVenue.id, {
							name: linkedVenue.name,
							address: linkedVenue.address,
							clubIds: [...linkedVenue.clubIds, clubId],
						});
						setLinking(false);
						await load();
						handleNotification(SUCCESSFUL_OPERATION, "success");
					} catch (e) {
						error(e);
					} finally {
						setLoading(false);
					}
				}}
			/>
			<VLFModal
				open={Boolean(editing)}
				title={editing?.id ? "Modifica campo" : "Nuovo campo"}
				close={() => setEditing(null)}
				disabled={loading || !editing?.name?.trim()}
				confirm={save}
				content={
					editing && (
						<>
							<VLFTextField
								label="Nome"
								required
								value={editing.name}
								onChange={(e) => setEditing({...editing, name: e.target.value})}
							/>
							<VLFTextField
								label="Indirizzo completo"
								value={editing.address || ""}
								onChange={(e) =>
									setEditing({...editing, address: e.target.value})
								}
							/>
							{myClubId && (
								<FormControlLabel
									control={
										<Checkbox
											checked={editing.clubIds.includes(myClubId)}
											onChange={(e) =>
												setEditing({
													...editing,
													clubIds: e.target.checked
														? [...new Set([...editing.clubIds, myClubId])]
														: editing.clubIds.filter((id) => id !== myClubId),
												})
											}
										/>
									}
									label="Campo del mio club"
								/>
							)}
							<VLFSelectNew
								label="Club associati"
								isMulti
								options={clubs.map((c) => ({value: c.id, label: c.name}))}
								value={editing.clubIds.map((id) => ({
									value: id,
									label: clubs.find((c) => c.id === id)?.name || id,
								}))}
								onChange={(values) =>
									setEditing({
										...editing,
										clubIds: (values || []).map((v) => v.value),
									})
								}
							/>
						</>
					)
				}
			/>
			<VLFModal
				open={Boolean(deleting)}
				title={clubId ? "Rimuovi associazione" : "Eliminazione campo"}
				content={
					clubId
						? "Rimuovere il campo dall’elenco del club? Il campo e gli eventi rimangono disponibili."
						: "Eliminare questo campo? I campi usati negli eventi non possono essere eliminati."
				}
				close={() => setDeleting(null)}
				disabled={loading}
				confirmLabel="Conferma"
				confirm={async () => {
					setLoading(true);
					try {
						if (clubId)
							await VenueAPI.update(deleting.id, {
								name: deleting.name,
								address: deleting.address,
								clubIds: deleting.clubIds.filter((id) => id !== clubId),
							});
						else await VenueAPI.delete(deleting.id);
						setDeleting(null);
						await load();
						handleNotification(SUCCESSFUL_OPERATION, "success");
					} catch (e) {
						error(e);
					} finally {
						setLoading(false);
					}
				}}
			/>
		</>
	);
}
